import React from 'react';
import { useHistory } from 'react-router-dom';
import { InSeparator, InterPoint } from '@saviynt/common';
import {
  Box,
  Button,
  Icon,
  ModalPageHeader,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { ModuleDetails } from '../../models/PamModels';
import { hasSelectedRoles } from '../../utilities';

function ModalFormHeader({
  title,
  manageItemsText,
  endpointDetails,
  selectedRoles,
  setIsModalOpen,
  dataTestId,
}) {
  const history = useHistory();

  const selectedRolesCount = selectedRoles?.length;

  const closeHandler = () => {
    history.goBack();
  };

  const getTitle = () => {
    if (hasSelectedRoles(selectedRoles)) {
      return `${title} (${selectedRolesCount})`;
    }

    return title;
  };

  const getDisplayName = () => {
    if (endpointDetails?.type === 'role')
      return endpointDetails?.roleDisplayName || endpointDetails?.roleName;

    return (
      endpointDetails?.endpointDisplayName ||
      endpointDetails?.endpointName ||
      endpointDetails?.displayName
    );
  };

  const getAssetOrRoleType = () => {
    if (endpointDetails?.type === 'role')
      return (
        <Typography kind='body2' className='ModalPage-subHeadingContentBody'>
          {endpointDetails?.roleType}
        </Typography>
      );

    return (
      <>
        <Icon
          kind={endpointDetails?.assetType}
          color='neutral-000'
          size='small'
        />
        <Typography kind='body2' className='ModalPage-subHeadingContentBody'>
          {endpointDetails?.assetType}
        </Typography>
      </>
    );
  };

  const getSubHeadingForBulkRoleSelect = () => {
    const roleNames = selectedRoles
      .map((role) => role?.roleDisplayName || role?.roleName)
      .join(', ');

    return (
      <Tooltip
        text={roleNames}
        dataTheme='dark'
        placement='bottom'
        maxWidth='50rem'
        trigger={
          <Typography
            kind='body2'
            lineLimit={1}
            tooltipConfig={{
              isDisabled: true,
            }}>
            {selectedRoles.map((role, index) => (
              <React.Fragment key={role.roleKey}>
                {role?.roleDisplayName || role?.roleName}
                {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                {index !== selectedRoles.length - 1 && ' • '}
              </React.Fragment>
            ))}
          </Typography>
        }
      />
    );
  };

  const getSubHeadingContent = () => (
    <Box tag='div' className='ModalPage-subHeadingContent'>
      {getDisplayName() && (
        <Typography kind='body2' className='ModalPage-subHeadingContentBody'>
          {getDisplayName()}
        </Typography>
      )}
      <InterPoint color='neutralSubtlest' />
      {getAssetOrRoleType()}
      {endpointDetails?.platformType && (
        <>
          <InSeparator color='neutralInverse' />
          <Typography kind='body2' className='ModalPage-subHeadingContentBody'>
            {endpointDetails?.platformType}
          </Typography>
        </>
      )}
    </Box>
  );

  const getManageItemsButton = () => (
    <Button
      type='button'
      kind='outlined'
      size='medium'
      onClick={() => setIsModalOpen(true)}>
      {manageItemsText}
    </Button>
  );

  return (
    <ModalPageHeader
      title={getTitle()}
      subHeadingContent={
        hasSelectedRoles(selectedRoles)
          ? getSubHeadingForBulkRoleSelect()
          : getSubHeadingContent()
      }
      trailingContent={
        hasSelectedRoles(selectedRoles) && getManageItemsButton()
      }
      closeHandler={() => closeHandler()}
      className='ModalPage-header'
      data-testid={dataTestId}
    />
  );
}

ModalFormHeader.propTypes = {
  title: PropTypes.string,
  manageItemsText: PropTypes.string,
  selectedRoles: PropTypes.arrayOf(
    PropTypes.shape({
      roleName: PropTypes.string,
      roleDisplayName: PropTypes.string,
      roleDescription: PropTypes.string,
      platformType: PropTypes.string,
    })
  ).isRequired,
  endpointDetails: ModuleDetails,
  dataTestId: PropTypes.string,
  setIsModalOpen: PropTypes.func,
};

ModalFormHeader.defaultProps = {
  title: null,
  manageItemsText: null,
  endpointDetails: null,
  dataTestId: null,
  setIsModalOpen: null,
};

export default ModalFormHeader;
